import * as Sentry from "@sentry/react";
import { ehRotaComSegredoNaUrl, ocultarSegredosNoObjeto } from "@/lib/ocultarSegredos";

const apiBaseUrl =
  (typeof window !== "undefined" && (window as any).__ENV__?.VITE_API_URL) ||
  import.meta.env.VITE_API_URL ||
  "http://localhost:3001/api";

const sentryDsn =
  (typeof window !== "undefined" && (window as any).__ENV__?.VITE_SENTRY_DSN) ||
  import.meta.env.VITE_SENTRY_DSN;

const sentryRelease =
  (typeof window !== "undefined" && (window as any).__ENV__?.VITE_SENTRY_RELEASE) ||
  import.meta.env.VITE_SENTRY_RELEASE;

// O link de redefinição de senha (`/redefinir-senha?token=...`) leva um JWT na URL: ele não pode ir para o Sentry
// (URL do evento, breadcrumbs de navegação, transações e Session Replay carregam a query inteira).
const naRotaComSegredo = typeof window !== "undefined" && ehRotaComSegredoNaUrl(window.location.pathname);

const redactSensitiveData = (value: unknown): unknown => {
  if (!value || typeof value !== "object") return value;

  const redacted = { ...(value as Record<string, unknown>) };
  for (const key of Object.keys(redacted)) {
    if (/authorization|cookie|token|password|senha|secret/i.test(key)) {
      redacted[key] = "[Filtered]";
    }
  }
  return redacted;
};

Sentry.init({
  dsn: sentryDsn,
  environment: import.meta.env.MODE,
  enabled: Boolean(sentryDsn),
  release: sentryRelease,

  ignoreErrors: [
    "ResizeObserver loop limit exceeded",
    "ResizeObserver loop completed with undelivered notifications.",
    "Non-Error promise rejection captured",
  ],
  denyUrls: [
    /extensions\//i,
    /^chrome:\/\//i,
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i,
    /^safari-extension:\/\//i,
  ],

  integrations: [
    Sentry.browserTracingIntegration(),
    // Replay grava a URL inicial da sessão; nessa rota ele nem é ligado.
    ...(naRotaComSegredo
      ? []
      : [
          Sentry.replayIntegration({
            blockAllMedia: true,
            maskAllText: true,
          }),
        ]),
  ],

  beforeSend(event) {
    if (event.request?.headers) {
      event.request.headers = redactSensitiveData(event.request.headers) as Record<string, string>;
    }
    if (event.request?.data) {
      event.request.data = redactSensitiveData(event.request.data);
    }
    return ocultarSegredosNoObjeto(event);
  },
  beforeSendTransaction: (event) => ocultarSegredosNoObjeto(event),
  beforeBreadcrumb: (breadcrumb) => ocultarSegredosNoObjeto(breadcrumb),

  tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0,
  replaysSessionSampleRate: import.meta.env.PROD ? 0.05 : 0,
  replaysOnErrorSampleRate: 1.0,

  tracePropagationTargets: [
    /^http:\/\/localhost:3001\/api/,
    apiBaseUrl,
  ],
});

if (import.meta.env.DEV && typeof window !== "undefined") {
  (window as any).triggerSentryTestError = () => {
    throw new Error("Sentry frontend test");
  };
}
